// Deposit Settlement Receipt — browser-print-to-PDF.
//
// Mirrors the pattern used by utils/agreement.js: build a print-
// friendly HTML page, open it in a new tab as a Blob URL, the page
// fires window.print() on load. The user picks "Save as PDF" in the
// browser dialog. No server-side PDF library needed.
//
// All photos referenced here use Supabase Storage public URLs that
// the print process will fetch and embed inline.

// HTML-escape free-text fields so caption / notes / reasons can't
// break out of attributes or inject script. Conservative replace.
const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Strict data-URL check for signature images — exactly the same regex
// the server uses to validate signatures (server/routes/vacate.js).
const SIG_DATA_URL_RE = /^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=]+$/;
const isValidSig = (s) => typeof s === 'string' && SIG_DATA_URL_RE.test(s);

// Paise → "₹1,234" — same fixed-point handling as the UI.
const fmtPaise = (paise) => "₹" + Math.round(Number(paise || 0) / 100).toLocaleString('en-IN');

// Number → English words (lakhs/crores), reused from agreement.js
// style. Kept inline so this util has no cross-file deps.
function numberToWords(n) {
    if (!Number.isFinite(n)) return '';
    n = Math.round(n);
    if (n === 0) return 'Zero Only';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const convert = (num) => {
        if (num < 20) return ones[num];
        if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
        if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' and ' + convert(num % 100) : '');
        if (num < 100000) return convert(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + convert(num % 1000) : '');
        if (num < 10000000) return convert(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + convert(num % 100000) : '');
        return convert(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + convert(num % 10000000) : '');
    };
    return convert(Math.abs(n)) + ' Rupees Only';
}

const REASON_LABEL = {
    damage: 'Property Damage',
    cleaning: 'Cleaning',
    painting: 'Painting',
    unpaid_bills: 'Unpaid Bills',
    other: 'Other',
};

/**
 * Build the full settlement receipt HTML. All inputs are optional —
 * missing values render as "—" so a partial settlement (e.g. no
 * tenant signature on an auto-accepted case) still produces a
 * legible PDF.
 *
 * @param {object} args
 *   - settlement: deposit_settlements row (the authoritative ledger)
 *   - vacate_request: vacate_requests row (for status/dispute context)
 *   - deductions: vacate_deductions rows (line items)
 *   - photos: vacate_photos rows (used to look up damage proofs)
 *   - lease, tenant, property, org: optional snapshots for the header
 */
export function generateSettlementHTML({
    settlement,
    vacate_request: vr,
    deductions = [],
    photos = [],
    lease,
    tenant,
    property,
    org,
}) {
    if (!settlement) throw new Error('settlement row required');

    const refundWords = numberToWords(Math.round(settlement.refund_amount / 100));
    const settledDate = settlement.settled_at
        ? new Date(settlement.settled_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
        : '—';

    const tenantName = settlement.tenant_name_snap || tenant?.name || '—';
    const tenantPhone = settlement.tenant_phone_snap || tenant?.ph || '—';
    const propertyAddr = settlement.property_addr_snap || property?.addr || '—';
    const orgName = org?.name || '';

    // Auto-accepted = no tenant signature on file but agreed_at is set.
    // Surfaces in the receipt so the legal context is clear.
    const wasAutoAccepted = !isValidSig(settlement.tenant_signature) && vr?.agreed_at && !vr?.disputed_at;

    // Find proof photo URLs per deduction (best-effort)
    const proofUrlFor = (d) => {
        let ids = [];
        try { ids = JSON.parse(d.proof_photo_ids || '[]'); } catch { ids = []; }
        return ids.map(id => photos.find(p => p.id === id)?.url).filter(Boolean);
    };

    const deductionRows = deductions.length === 0
        ? `<tr><td colspan="3" class="ded-empty">No deductions — full refund</td></tr>`
        : deductions.map((d, i) => `
            <tr>
              <td>
                <div class="ded-reason">${i + 1}. ${esc(REASON_LABEL[d.reason] || d.reason)}</div>
                ${d.description ? `<div class="ded-desc">${esc(d.description)}</div>` : ''}
                ${(() => {
                    const urls = proofUrlFor(d);
                    if (!urls.length) return '';
                    return `<div class="ded-proofs">${urls.map(u => `<img src="${esc(u)}" alt="proof" />`).join('')}</div>`;
                })()}
              </td>
              <td class="ded-amount">${fmtPaise(d.amount)}</td>
            </tr>
        `).join('');

    return `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8">
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Deposit Settlement Receipt — ${esc(tenantName)}</title>
    <style>
      @page { margin: 18mm 14mm; }
      body { font-family: 'Times New Roman', serif; padding: 0; line-height: 1.6; font-size: 13px; color: #222; }
      h1 { text-align: center; font-size: 22px; text-decoration: underline; margin: 0 0 4px; }
      .subtitle { text-align: center; font-size: 12px; color: #555; margin-bottom: 24px; }
      .meta { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
      .meta td { padding: 4px 8px; font-size: 12px; border-bottom: 1px dashed #ccc; vertical-align: top; }
      .meta td.k { color: #666; width: 35%; font-weight: 600; }
      h2 { font-size: 14px; margin: 18px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #333; }
      .ded-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
      .ded-table th { background: #f4f4f4; padding: 8px; text-align: left; font-size: 11px; border: 1px solid #ccc; }
      .ded-table td { padding: 8px; font-size: 12px; vertical-align: top; border: 1px solid #ddd; }
      .ded-reason { font-weight: 700; }
      .ded-desc { color: #555; font-size: 11px; margin-top: 2px; }
      .ded-empty { color: #888; font-style: italic; text-align: center; padding: 14px; }
      .ded-amount { text-align: right; white-space: nowrap; font-weight: 700; color: #b91c1c; }
      .ded-proofs { display: flex; gap: 4px; margin-top: 6px; }
      .ded-proofs img { max-height: 60px; max-width: 80px; border: 1px solid #ccc; border-radius: 3px; object-fit: cover; }
      .totals { width: 100%; border-collapse: collapse; margin-top: 6px; }
      .totals td { padding: 6px 8px; font-size: 12px; }
      .totals tr.refund td { border-top: 2px solid #000; font-size: 14px; font-weight: 700; padding-top: 10px; }
      .totals td.r { text-align: right; }
      .words { background: #f8f8f8; padding: 8px 12px; border-left: 4px solid #333; font-style: italic; font-size: 12px; margin: 12px 0 18px; }
      .ack { font-size: 12px; line-height: 1.7; margin-bottom: 22px; text-align: justify; }
      .sig-block { display: flex; justify-content: space-between; margin-top: 30px; gap: 30px; }
      .sig-box { flex: 1; text-align: center; min-width: 0; }
      .sig-img { max-height: 60px; background: #fff; padding: 4px; border-bottom: 1px solid #000; display: block; margin: 0 auto 4px; }
      .sig-placeholder { border-bottom: 1px solid #000; min-height: 60px; margin-bottom: 4px; }
      .sig-label { font-size: 11px; color: #555; }
      .footer { margin-top: 36px; font-size: 10px; color: #888; text-align: center; border-top: 1px solid #ddd; padding-top: 10px; }
      .auto-accepted { background: #fff7ed; border: 1px solid #fb923c; color: #9a3412; padding: 8px 12px; border-radius: 4px; font-size: 11px; margin-bottom: 12px; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
  </head><body>
    <h1>DEPOSIT SETTLEMENT RECEIPT</h1>
    <div class="subtitle">
      ${orgName ? esc(orgName) + ' &nbsp;&middot;&nbsp; ' : ''}Settled on ${esc(settledDate)}
    </div>

    ${wasAutoAccepted ? `
      <div class="auto-accepted">
        ⓘ This settlement was auto-accepted on behalf of the tenant after no response within the dispute window.
      </div>
    ` : ''}

    ${vr?.admin_resolution_note ? `
      <div style="background:#eef2ff; border:1px solid #4338ca; color:#312e81; padding:10px 14px; border-radius:4px; margin-bottom:14px;">
        <b>⚖️ Admin Ruling on Dispute</b><br/>
        <div style="font-size:11px; margin-top:4px; white-space:pre-wrap;">${esc(vr.admin_resolution_note)}</div>
      </div>
    ` : ''}

    <table class="meta">
      <tr><td class="k">Tenant</td><td>${esc(tenantName)} &nbsp;·&nbsp; ${esc(tenantPhone)}</td></tr>
      <tr><td class="k">Property</td><td>${esc(propertyAddr)}</td></tr>
      <tr><td class="k">Lease ID</td><td>${esc(settlement.lease_id || vr?.lease_id || '—')}</td></tr>
      <tr><td class="k">Settlement Reference</td><td>${esc(settlement.id)}</td></tr>
      <tr><td class="k">Resolution</td><td>${esc((settlement.resolution || 'agreed').replace(/_/g, ' '))}</td></tr>
    </table>

    <h2>Deduction Breakdown</h2>
    <table class="ded-table">
      <thead><tr><th>Reason</th><th style="width:120px;">Amount</th></tr></thead>
      <tbody>${deductionRows}</tbody>
    </table>

    <table class="totals">
      <tr><td>Security Deposit (paid by tenant)</td><td class="r">${fmtPaise(settlement.deposit_amount)}</td></tr>
      <tr><td>Less: Total Deductions</td><td class="r" style="color:#b91c1c;">−${fmtPaise(settlement.total_deductions)}</td></tr>
      <tr class="refund"><td>Refund payable to tenant</td><td class="r" style="color:#16a34a;">${fmtPaise(settlement.refund_amount)}</td></tr>
    </table>

    <div class="words">
      <b>In words:</b> ${esc(refundWords)}
    </div>

    <h2>Acknowledgement</h2>
    <p class="ack">
      The Landlord and the Tenant hereby acknowledge that the security deposit accounting recorded above
      is true and complete. The Tenant confirms receipt of the refund amount stated above and the Landlord
      confirms that no further claim shall lie against the Tenant's deposit for the lease referenced.
      This settlement is e-signed and forms a legally binding record under the Information Technology
      Act, 2000.
    </p>

    <div class="sig-block">
      <div class="sig-box">
        ${isValidSig(settlement.tenant_signature)
            ? `<img class="sig-img" src="${esc(settlement.tenant_signature)}" alt="tenant signature" />`
            : `<div class="sig-placeholder"></div>`}
        <div class="sig-label"><b>${esc(tenantName)}</b><br>Tenant</div>
      </div>
      <div class="sig-box">
        ${isValidSig(settlement.landlord_signature)
            ? `<img class="sig-img" src="${esc(settlement.landlord_signature)}" alt="landlord signature" />`
            : `<div class="sig-placeholder"></div>`}
        <div class="sig-label"><b>${orgName ? esc(orgName) : 'Landlord'}</b><br>Landlord / Authorised Signatory</div>
      </div>
    </div>

    <div class="footer">
      Computer-generated. Vacate request: ${esc(vr?.id || '—')} · Lease: ${esc(settlement.lease_id || '—')}
    </div>

    <script>window.onload = () => { setTimeout(() => window.print(), 300); }</script>
  </body></html>`;
}

/**
 * Open the settlement PDF in a new tab. Same blob-URL pattern as
 * agreement.js. 60s revoke window gives the browser time to download
 * the embedded images before we release the URL.
 */
export function openSettlementPdf(args) {
    const html = generateSettlementHTML(args);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    // Some mobile browsers (Safari standalone PWAs) block popups —
    // fall back to a same-tab navigation so the print dialog still appears.
    if (!win) window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}
